import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSession } from '@/domains/auth/services/auth.service';
import {
  ENTITY_ROUTE_TO_TYPE,
  getEntityConfig,
} from '@/domains/company/constants/entity.config';
import {
  fetchCompanyProfile,
  fetchEntityOrders,
  fetchEntityStats,
  updateCompanyProfile,
} from '@/domains/company/services/company.service';
import {
  MOCK_CLIENTS,
  MOCK_STOCK,
  MOCK_TRANSACTIONS,
} from '@/domains/company/mock/company.mock';
import type { CompanyProfile, EntityOrder, EntityStats } from '@/shared/types/company.types';
import { PremiumDashboardLayout, StatCard } from '@/components/dashboard/PremiumDashboardLayout';
import { SmartDataTable, type Column } from '@/components/ui/SmartDataTable';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { InputAmount } from '@/components/ui/InputAmount';
import { useCurrency } from '@/shared/context/CurrencyContext';
import { formatAmount } from '@/utils/currency';
import { handleErrorApi, showSuccess } from '@/utils/handleErrorApi';
import { CURRENCY_LIST } from '@/shared/types/currency.types';
import { saveEntityType } from '@/domains/company/utils/entitySession';

const PAGE_TITLES: Record<string, string> = {
  accueil: 'Vue d\'ensemble',
  commandes: 'Gestion des commandes',
  clients: 'Clients réguliers',
  stock: 'Stock & inventaire',
  stockage: 'Colis en stockage',
  transactions: 'Transactions financières',
  devis: 'Devis déménagement',
  missions: 'Missions humanitaires',
  equipe: 'Équipe logistique',
  profil: 'Profil de l\'organisation',
  finances: 'Finances & paiements',
};

export default function EntityDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currency, setCurrency } = useCurrency();

  const entityType = ENTITY_ROUTE_TO_TYPE[location.pathname];
  const config = entityType ? getEntityConfig(entityType) : null;

  const [activeTab, setActiveTab] = useState('accueil');
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [stats, setStats] = useState<EntityStats | null>(null);
  const [orders, setOrders] = useState<EntityOrder[]>([]);
  const [ordersMeta, setOrdersMeta] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0, from: 0, to: 0 });
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [editProfile, setEditProfile] = useState<Partial<CompanyProfile>>({});
  const [budgetAmount, setBudgetAmount] = useState(500000);

  const loadProfile = useCallback(async () => {
    const session = getSession();
    if (!session || !entityType) return;
    try {
      const [prof, statsData] = await Promise.all([
        fetchCompanyProfile(session.user.id, entityType),
        fetchEntityStats(),
      ]);
      setProfile(prof);
      setEditProfile(prof);
      setStats(statsData);
      saveEntityType(prof.entity_type);
    } catch (err) {
      handleErrorApi(err);
    }
  }, [entityType]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await fetchEntityOrders(page, perPage, search);
      setOrders(res.data);
      setOrdersMeta(res.meta);
    } catch (err) {
      handleErrorApi(err);
    } finally {
      setOrdersLoading(false);
    }
  }, [page, perPage, search]);

  useEffect(() => {
    const session = getSession();
    if (!session) { navigate('/login'); return; }
    if (!entityType || !config) { navigate('/'); return; }
    loadProfile();
  }, [navigate, entityType, config, loadProfile]);

  useEffect(() => {
    if (activeTab === 'commandes') loadOrders();
  }, [activeTab, loadOrders]);

  useEffect(() => {
    const t = setTimeout(() => { if (activeTab === 'commandes') loadOrders(); }, 300);
    return () => clearTimeout(t);
  }, [search, page, perPage, activeTab, loadOrders]);

  const handleSaveProfile = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const updated = await updateCompanyProfile(profile.id, editProfile);
      setProfile(updated);
      showSuccess('Profil mis à jour');
    } catch (err) {
      handleErrorApi(err);
    } finally {
      setSaving(false);
    }
  };

  if (!config || !entityType) return null;

  const orderColumns: Column<EntityOrder>[] = [
    { key: 'code_suivi', label: 'Référence', render: (r) => <span className="font-semibold text-teal-700">{r.code_suivi}</span> },
    { key: 'client_nom', label: 'Client' },
    { key: 'type_livraison', label: 'Type' },
    { key: 'adresse_livraison', label: 'Destination', className: 'max-w-[180px] truncate' },
    {
      key: 'montant',
      label: 'Montant',
      render: (r) => formatAmount(r.montant, currency),
    },
    { key: 'statut', label: 'Statut', render: (r) => <StatusBadge status={r.statut} /> },
    { key: 'livreur_nom', label: 'Livreur', render: (r) => r.livreur_nom ?? '—' },
  ];

  return (
    <PremiumDashboardLayout
      tabs={config.tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      pageTitle={PAGE_TITLES[activeTab] ?? config.label}
      roleLabel={config.label}
      accent={config.accent}
      userName={profile?.organization_name}
      userZone={profile?.address ?? undefined}
      userInitial={profile?.organization_name?.[0]}
      mobileNavTabIds={config.tabs.slice(0, 4).map((t) => t.id)}
    >
      {activeTab === 'accueil' && stats && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-2xl p-6 text-white border border-slate-800">
            <span className="text-[10px] font-bold uppercase tracking-wider text-teal-400">{config.label}</span>
            <h2 className="text-lg font-bold mt-1">{profile?.organization_name}</h2>
            <p className="text-xs text-slate-400 mt-1">{config.description}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon="ri-truck-line" label="En cours" value={stats.commandes_en_cours} color="teal" />
            <StatCard icon="ri-check-double-line" label="Terminées" value={stats.commandes_terminees} color="emerald" />
            <StatCard icon="ri-wallet-3-line" label="Revenus mois" value={formatAmount(stats.revenus_mois, currency, { compact: true })} color="amber" />
            <StatCard icon="ri-star-line" label="Satisfaction" value={`${stats.satisfaction}/5`} color="blue" />
          </div>
          <button
            onClick={() => setActiveTab('commandes')}
            className="w-full py-3 bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold rounded-xl transition-colors"
          >
            <i className="ri-add-line mr-1" /> Nouvelle expédition
          </button>
        </div>
      )}

      {activeTab === 'commandes' && (
        <SmartDataTable
          title="Commandes & livraisons"
          columns={orderColumns}
          data={orders}
          rowKey={(r) => r.id}
          meta={ordersMeta}
          loading={ordersLoading}
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          onPageChange={setPage}
          onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
          emptyMessage="Aucune commande"
        />
      )}

      {activeTab === 'clients' && (
        <SmartDataTable
          title="Clients réguliers"
          columns={[
            { key: 'name', label: 'Nom', render: (r) => <span className="font-semibold">{r.name}</span> },
            { key: 'type', label: 'Type' },
            { key: 'address', label: 'Adresse' },
            { key: 'frequence', label: 'Fréquence' },
          ]}
          data={MOCK_CLIENTS}
          rowKey={(r) => r.id}
          emptyMessage="Aucun client"
        />
      )}

      {activeTab === 'stock' && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center text-slate-500 text-sm">
          <i className="ri-archive-line text-3xl block mb-2 text-slate-300" />
          Module stock — synchronisation catalogue en attente backend
        </div>
      )}

      {activeTab === 'stockage' && (
        <SmartDataTable
          title="Colis en point relais"
          columns={[
            { key: 'code', label: 'Code colis' },
            { key: 'client', label: 'Client' },
            { key: 'statut', label: 'Statut', render: (r) => <StatusBadge status={r.statut} label={r.statut === 'en_stock' ? 'En stock' : 'À retirer'} /> },
            { key: 'arrive', label: 'Arrivée' },
          ]}
          data={MOCK_STOCK}
          rowKey={(r) => r.id}
        />
      )}

      {activeTab === 'transactions' && (
        <SmartDataTable
          title="Transactions Mobile Money & COD"
          columns={[
            { key: 'ref', label: 'Référence' },
            { key: 'gateway', label: 'Passerelle' },
            { key: 'montant', label: 'Montant', render: (r) => formatAmount(r.montant, currency) },
            { key: 'statut', label: 'Statut', render: (r) => <StatusBadge status={r.statut} /> },
            { key: 'date', label: 'Date' },
          ]}
          data={MOCK_TRANSACTIONS}
          rowKey={(r) => r.id}
        />
      )}

      {activeTab === 'devis' && (
        <div className="space-y-4">
          <InputAmount label="Estimation du déménagement" value={budgetAmount} onChange={setBudgetAmount} showCurrencySelector currency={currency} onCurrencyChange={setCurrency} />
          <button className="w-full py-3 bg-amber-600 text-white text-sm font-bold rounded-xl">Demander un devis</button>
        </div>
      )}

      {activeTab === 'missions' && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 text-sm text-emerald-800">
          <i className="ri-hand-heart-line text-2xl block mb-2" />
          Missions humanitaires — planification et suivi des distributions (mock, backend à venir).
        </div>
      )}

      {activeTab === 'equipe' && (
        <SmartDataTable
          title="Équipe logistique"
          columns={[
            { key: 'name', label: 'Membre', render: () => 'Responsable logistique' },
            { key: 'role', label: 'Rôle', render: () => 'Coordinateur' },
            { key: 'status', label: 'Statut', render: () => <StatusBadge status="active" label="Actif" /> },
          ]}
          data={[{ id: 1 }]}
          rowKey={(r) => r.id}
        />
      )}

      {activeTab === 'profil' && profile && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nom de l'organisation" value={editProfile.organization_name ?? ''} onChange={(v) => setEditProfile({ ...editProfile, organization_name: v })} />
            <Field label="Type d'institution" value={editProfile.institution_type ?? ''} onChange={(v) => setEditProfile({ ...editProfile, institution_type: v })} />
            <Field label="Responsable" value={editProfile.responsible_name ?? ''} onChange={(v) => setEditProfile({ ...editProfile, responsible_name: v })} />
            <Field label="Contact responsable" value={editProfile.responsible_contact ?? ''} onChange={(v) => setEditProfile({ ...editProfile, responsible_contact: v })} />
            <Field label="Volume mensuel" value={editProfile.monthly_volume ?? ''} onChange={(v) => setEditProfile({ ...editProfile, monthly_volume: v })} />
            <Field label="Site web" value={editProfile.website ?? ''} onChange={(v) => setEditProfile({ ...editProfile, website: v })} />
            <Field label="Adresse" value={editProfile.address ?? ''} onChange={(v) => setEditProfile({ ...editProfile, address: v })} className="md:col-span-2" />
            <Field label="Point de repère" value={editProfile.landmark ?? ''} onChange={(v) => setEditProfile({ ...editProfile, landmark: v })} />
            <Field label="Types de marchandises" value={editProfile.goods_types ?? ''} onChange={(v) => setEditProfile({ ...editProfile, goods_types: v })} placeholder="Documents,Médicaments,…" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Instructions spécifiques</label>
            <textarea
              value={editProfile.specific_instructions ?? ''}
              onChange={(e) => setEditProfile({ ...editProfile, specific_instructions: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
            />
          </div>
          <button
            disabled={saving}
            onClick={handleSaveProfile}
            className="px-6 py-2.5 bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold rounded-xl disabled:opacity-50"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer le profil'}
          </button>
        </div>
      )}

      {activeTab === 'finances' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <label className="block text-xs font-semibold text-slate-600 mb-2">Devise d'affichage</label>
            <SearchableSelect
              options={CURRENCY_LIST.map((c) => ({ value: c.code, label: `${c.label} (${c.code})` }))}
              value={currency}
              onChange={(v) => setCurrency(v as typeof currency)}
            />
          </div>
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard icon="ri-wallet-3-line" label="Revenus du mois" value={formatAmount(stats.revenus_mois, currency)} color="emerald" />
              <StatCard icon="ri-hand-coin-line" label="COD en attente" value={formatAmount(120000, currency)} color="amber" />
            </div>
          )}
          <InputAmount label="Montant d'une nouvelle transaction" value={budgetAmount} onChange={setBudgetAmount} />
        </div>
      )}
    </PremiumDashboardLayout>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  className = '',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
      />
    </div>
  );
}
